import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV } from "../../data/site";
import { CTAButton } from "../common/CTAButton";

const Wordmark = ({ className = "" }) => (
  <Link to="/" data-testid="brand-logo" title="AdX" aria-label="AdX - home" className={`group flex items-center gap-2.5 ${className}`}>
    <img
      src="/adx-logo.png"
      title="AdX"
      alt="AdX - residential digital advertising"
      className="h-12 w-12 rounded-md shadow-sm transition-transform duration-300 group-hover:-translate-y-0.5"
    />
  </Link>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const onHero = !scrolled && !open;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,padding] duration-300 ${
        scrolled || open ? "glass-nav border-b border-ink/10 py-3" : "bg-transparent py-5"
      }`}
    >
      <div className={`container-px flex items-center justify-between ${onHero ? "text-white" : "text-ink"}`}>
        <Wordmark />

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.to.replace("/", "")}`}
              className={({ isActive }) =>
                `relative px-3.5 py-2 text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? onHero ? "text-white" : "text-ink"
                    : onHero ? "text-white/60 hover:text-white" : "text-ink/60 hover:text-ink"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {item.label}
                  {isActive && (
                    <span className="absolute left-3.5 right-3.5 -bottom-0.5 h-px bg-teal" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <CTAButton to="/contact" variant={onHero ? "primary" : "dark"} data-testid="header-cta">
            Get Started
          </CTAButton>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className={`lg:hidden p-2 -mr-2 ${onHero ? "text-white" : "text-ink"}`}
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden glass-nav border-t border-ink/10 mt-3">
          <nav className="container-px py-6 flex flex-col gap-1 text-ink">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className="py-3 text-lg font-display font-600 border-b border-ink/5"
              >
                {item.label}
              </NavLink>
            ))}
            <CTAButton to="/contact" variant="dark" className="mt-4 w-full">
              Get Started
            </CTAButton>
          </nav>
        </div>
      )}
    </header>
  );
};
